import { FormControl, MenuItem, Select } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import type { ChartTypeCode, ChartTypeOption } from '../api/chartConfig';
import { CHART_TYPE_DISPLAY_NAMES } from '../api/chartConfig';
import { chartTypeSelectSx, srOnly } from '../styles/chartReader.styles';

type ChartTypeDropdownProps = {
    options: ChartTypeOption[];
    value: ChartTypeCode | '';
    onChange: (value: ChartTypeCode) => void;
};

export type ChartTypeDropdownHandle = { focus: () => void };

export const ChartTypeDropdown = forwardRef<ChartTypeDropdownHandle, ChartTypeDropdownProps>(
    ({ options, value, onChange }, ref) => {
        const [announcement, setAnnouncement] = useState('');
        const formControlRef = useRef<HTMLDivElement>(null);

        const isDisabled = options.length === 0;

        useImperativeHandle(ref, () => ({
            focus: () => {
                // MUI Select renders the visible trigger with role="combobox" inside the FormControl
                const trigger = formControlRef.current?.querySelector<HTMLElement>('[role="combobox"]');
                trigger?.focus();
            },
        }));

        const handleChange = (event: SelectChangeEvent<string>) => {
            const selected = event.target.value as ChartTypeCode;
            onChange(selected);
            // Clear first so NVDA re-announces even if the same item is re-selected
            setAnnouncement('');
            setTimeout(() => setAnnouncement(`Chart type selected: ${CHART_TYPE_DISPLAY_NAMES[selected]}`), 50);
        };

        // Prompt the user the first time focus lands here (no chart type picked yet);
        // switch to a quieter label once they've made a selection.
        const ariaLabel = value ? 'Chart type' : 'Select Chart Type';

        return (
            <>
                {/* Assertive live region — fires immediately on selection */}
                <div aria-live='assertive' aria-atomic='true' style={srOnly}>
                    {announcement}
                </div>

                <FormControl ref={formControlRef} fullWidth size='small' disabled={isDisabled} sx={chartTypeSelectSx}>
                    <Select
                        id='chart-type-select'
                        value={value}
                        onChange={handleChange}
                        displayEmpty
                        renderValue={(v) => (v ? CHART_TYPE_DISPLAY_NAMES[v as ChartTypeCode] : 'Select a chart type')}
                        // aria-label must sit on the visible role="combobox" display element
                        // (what actually receives focus) — not just the hidden input. Without
                        // this, NVDA finds no accessible name on focus and reads the document
                        // title ("CFRA Accessibility document") instead of the prompt.
                        inputProps={{ 'aria-label': ariaLabel }}
                        SelectDisplayProps={{ 'aria-label': ariaLabel }}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    border: '1px solid #E4E4E4',
                                    borderRadius: '4px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                },
                            },
                        }}>
                        {options.map(({ code, label }) => (
                            <MenuItem key={code} value={code}>
                                {label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </>
        );
    },
);
